import { CharacterMetadata, ContentState, EditorState, RichUtils, SelectionState, ContentBlock, genKey } from 'draft-js'
import { List, OrderedMap } from 'immutable'
import { useState, useRef, useLayoutEffect } from 'react'

// Remove the atomic block carrying `entityKey` from a content state, keeping the
// block map valid (Draft.js requires at least one block) and retargeting any
// selection that pointed at the removed block. Used to purge transient upload
// progress blocks from historical content states.
const removeEntityBlockFromContent = (contentState: ContentState, entityKey: string): ContentState => {
  const blockMap = contentState.getBlockMap()
  let targetKey: string | null = null
  for (const key of blockMap.keySeq().toArray()) {
    const block = blockMap.get(key)
    if (block && block.getType() === 'atomic' && block.getEntityAt(0) === entityKey) {
      targetKey = key
      break
    }
  }

  if (!targetKey) return contentState

  let newBlockMap = blockMap.delete(targetKey)
  if (newBlockMap.size === 0) {
    const emptyBlock = new ContentBlock({ key: genKey(), type: 'unstyled', text: '', characterList: List() })
    newBlockMap = newBlockMap.set(emptyBlock.getKey(), emptyBlock)
  }

  const safeKey = newBlockMap.keySeq().first() as string
  const retarget = (selection: SelectionState): SelectionState =>
    selection.getAnchorKey() === targetKey || selection.getFocusKey() === targetKey
      ? SelectionState.createEmpty(safeKey)
      : selection

  return contentState.merge({
    blockMap: newBlockMap,
    selectionBefore: retarget(contentState.getSelectionBefore()),
    selectionAfter: retarget(contentState.getSelectionAfter())
  }) as ContentState
}

// Scrub every trace of a resolved upload's progress block from the undo/redo
// history, so pressing CTRL+Z after an upload finishes reverts the finished
// media rather than re-surfacing a now-untracked progress bar (issue #10405).
const scrubProgressFromHistory = (editorState: EditorState, entityKey: string): EditorState => {
  const scrub = (content: ContentState | undefined): ContentState | undefined =>
    content ? removeEntityBlockFromContent(content, entityKey) : content
  const undoStack = editorState.getUndoStack().map(scrub)
  const redoStack = editorState.getRedoStack().map(scrub)
  return EditorState.set(editorState, { undoStack, redoStack })
}

const useUploadProgress = ({
  editorState,
  insertAtomicBlockWithoutExtraLine,
  videoEntityType = 'draft-js-video-plugin-video'
}: {
  editorState: EditorState
  insertAtomicBlockWithoutExtraLine: (editorState: EditorState, entityKey: string) => EditorState
  videoEntityType?: string
}) => {
  const [uploadProgressMap, setUploadProgressMap] = useState<
    Map<string, { progress: number; mediaType: 'image' | 'video' | 'file'; filename: string }>
  >(new Map())
  const editorStateRef = useRef<EditorState | undefined>(editorState)

  // Keep ref in sync with state. This must use useLayoutEffect (not useEffect)
  // so the ref is updated synchronously during commit before any upload-completion
  // handler reads from or manually updates it. If the ref update is deferred to a
  // passive effect, that effect can later write an older editorState back into the
  // ref and overwrite a newer manual update, causing earlier progress bars to never
  // be replaced.
  useLayoutEffect(() => {
    editorStateRef.current = editorState
  }, [editorState])

  const getEditorState = () => editorStateRef.current!

  const insertUploadProgress = (
    currentEditorState: EditorState,
    mediaType: 'image' | 'video' | 'file',
    filename: string
  ): { editorState: EditorState; entityKey: string } => {
    let stateToInsertInto = currentEditorState

    // If the selection is inside an atomic block, move it to the next unstyled block
    // or create one. This is critical for multi-file insertions — after inserting the
    // first progress bar, the cursor often lands on the atomic block itself.
    if (RichUtils.getCurrentBlockType(stateToInsertInto) === 'atomic') {
      const content = stateToInsertInto.getCurrentContent()
      const selection = stateToInsertInto.getSelection()
      const currentKey = selection.getStartKey()
      const blockMap = content.getBlockMap()
      const blockKeys = blockMap.keySeq().toArray()
      const currentIndex = blockKeys.indexOf(currentKey)

      // Look for the next non-atomic block after the current one
      let targetKey: string | null = null
      for (let i = currentIndex + 1; i < blockKeys.length; i++) {
        const block = blockMap.get(blockKeys[i])
        if (block && block.getType() !== 'atomic') {
          targetKey = blockKeys[i]
          break
        }
      }

      if (targetKey) {
        // Move selection to the start of the found non-atomic block
        const newSelection = SelectionState.createEmpty(targetKey)
        stateToInsertInto = EditorState.forceSelection(stateToInsertInto, newSelection)
      } else {
        // No non-atomic block after — append a new empty block
        const newBlock = new ContentBlock({
          key: genKey(),
          type: 'unstyled',
          text: '',
          characterList: List()
        })
        const newBlockMap = blockMap.concat(OrderedMap([[newBlock.getKey(), newBlock]]))
        const newContent = content.merge({ blockMap: newBlockMap }) as typeof content
        stateToInsertInto = EditorState.push(stateToInsertInto, newContent, 'insert-fragment')
        const newSelection = SelectionState.createEmpty(newBlock.getKey())
        stateToInsertInto = EditorState.forceSelection(stateToInsertInto, newSelection)
      }
    }

    const contentState = stateToInsertInto.getCurrentContent()
    const contentStateWithEntity = contentState.createEntity('UPLOAD_PROGRESS', 'IMMUTABLE', {
      mediaType,
      filename,
      progress: 0
    })
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
    const withProgress = insertAtomicBlockWithoutExtraLine(stateToInsertInto, entityKey)

    // Progress bars are transient UI, so keep them out of the undo history: restore
    // the undo/redo stacks to what they were before insertion. Without this, undoing
    // after the upload finishes would land back on this progress-bar content state —
    // and since it's no longer tracked once the upload resolves, it renders a progress
    // bar stuck at 0% indefinitely (issue #10405).
    const newEditorState = EditorState.set(withProgress, {
      undoStack: currentEditorState.getUndoStack(),
      redoStack: currentEditorState.getRedoStack()
    })

    // Track in local state for re-renders
    setUploadProgressMap((prev) => {
      const next = new Map(prev)
      next.set(entityKey, { progress: 0, mediaType, filename })
      return next
    })

    return { editorState: newEditorState, entityKey }
  }

  const updateUploadProgress = (entityKey: string, progress: number) => {
    setUploadProgressMap((prev) => {
      const next = new Map(prev)
      const existing = next.get(entityKey)
      if (existing) {
        next.set(entityKey, { ...existing, progress })
      }
      return next
    })
  }

  const removeUploadProgressBlock = (progressEntityKey: string, currentEditorState: EditorState): EditorState => {
    const contentState = currentEditorState.getCurrentContent()
    const newContentState = removeEntityBlockFromContent(contentState, progressEntityKey)
    // Progress block not found — nothing to remove.
    if (newContentState === contentState) return currentEditorState
    return EditorState.push(currentEditorState, newContentState, 'remove-range')
  }

  const replaceUploadProgressWithMedia = (
    progressEntityKey: string,
    mediaType: 'image' | 'video' | 'file',
    mediaData: { url: string; filename?: string; extra?: any } | null
  ): EditorState => {
    const currentEditorState = getEditorState()!

    // Clean up tracking maps
    setUploadProgressMap((prev) => {
      const next = new Map(prev)
      next.delete(progressEntityKey)
      return next
    })

    // If upload failed, just remove the progress block
    if (!mediaData) {
      const cleaned = removeUploadProgressBlock(progressEntityKey, currentEditorState)
      return scrubProgressFromHistory(cleaned, progressEntityKey)
    }

    // Find the progress block in the block map
    const contentState = currentEditorState.getCurrentContent()
    const blockMap = contentState.getBlockMap()
    const blockKeys = blockMap.keySeq().toArray()
    let progressBlockKey: string | null = null

    for (const key of blockKeys) {
      const block = blockMap.get(key)
      if (block && block.getType() === 'atomic' && block.getEntityAt(0) === progressEntityKey) {
        progressBlockKey = key
        break
      }
    }

    // Fallback: if progress block not found, insert at current selection
    if (!progressBlockKey) {
      return scrubProgressFromHistory(
        insertMediaAtSelection(currentEditorState, mediaType, mediaData),
        progressEntityKey
      )
    }

    // Create the media entity
    let newEntityType: string
    let newEntityData: any

    if (mediaType === 'image') {
      newEntityType = 'IMAGE'
      newEntityData = { src: mediaData.url, ...mediaData.extra }
    } else if (mediaType === 'video') {
      newEntityType = videoEntityType
      newEntityData = { src: mediaData.url }
    } else {
      newEntityType = 'FILE'
      newEntityData = { src: mediaData.url, filename: mediaData.filename }
    }

    const contentWithEntity = contentState.createEntity(newEntityType, 'IMMUTABLE', newEntityData)
    const newEntityKey = contentWithEntity.getLastCreatedEntityKey()

    // Build a replacement atomic block carrying the new media entity
    const charData = CharacterMetadata.create({ entity: newEntityKey })
    const mediaBlock = new ContentBlock({
      key: progressBlockKey,
      type: 'atomic',
      text: ' ',
      characterList: List([charData])
    })

    // Swap the progress block for the media block in-place — no trailing empty
    // blocks are created, so consecutive media sit directly adjacent.
    const newBlockMap = contentWithEntity.getBlockMap().set(progressBlockKey, mediaBlock)
    const newContent = contentWithEntity.merge({ blockMap: newBlockMap }) as typeof contentWithEntity
    const withMedia = EditorState.push(currentEditorState, newContent, 'apply-entity')
    return scrubProgressFromHistory(withMedia, progressEntityKey)
  }

  const insertMediaAtSelection = (
    editorState: EditorState,
    mediaType: 'image' | 'video' | 'file',
    mediaData: { url: string; filename?: string; extra?: any }
  ): EditorState => {
    // Create the new media entity
    let newEntityType: string
    let newEntityData: any

    if (mediaType === 'image') {
      newEntityType = 'IMAGE'
      newEntityData = { src: mediaData.url, ...mediaData.extra }
    } else if (mediaType === 'video') {
      newEntityType = videoEntityType
      newEntityData = { src: mediaData.url }
    } else {
      newEntityType = 'FILE'
      newEntityData = { src: mediaData.url, filename: mediaData.filename }
    }

    const contentState = editorState.getCurrentContent()
    const contentWithNewEntity = contentState.createEntity(newEntityType, 'IMMUTABLE', newEntityData)
    const newEntityKey = contentWithNewEntity.getLastCreatedEntityKey()

    editorState = EditorState.set(editorState, { currentContent: contentWithNewEntity })
    return insertAtomicBlockWithoutExtraLine(editorState, newEntityKey)
  }

  return {
    insertUploadProgress,
    updateUploadProgress,
    replaceUploadProgressWithMedia,
    uploadProgressMap,
    editorStateRef
  }
}

export default useUploadProgress
